import React from 'react';
import {
  Toolbar,
  ToolGroup,
  Button,
  Select,
  SelectButton,
  DropdownMenu,
  MenuItem,
  FileMenuContainer,
  FileMenuButton,
  FileMenuDropdown,
  MenuDivider,
  ImageMenuContainer,
  ImageButton,
  ImageDropdown,
  ToolDropdown,
  ToolDropdownButton,
  ToolDropdownMenu,
  ToolItemRow,
  ToolDropdownButtonEnhanced,
  ContextMenu,
  ContextMenuItem,
  ColorSwatch
} from './EditorStyles';
import { fonts, fontSizes, colors, highlightColors } from './editorConstants';

// File Menu Component
export const FileMenu = ({ isOpen, setIsOpen, handleFileAction }) => {
  return (
    <FileMenuContainer>
      <FileMenuButton
        onClick={() => setIsOpen(!isOpen)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
      >
        File
      </FileMenuButton>
      <FileMenuDropdown $isOpen={isOpen}>
        <MenuItem onClick={() => handleFileAction('new')}>
          New
          <span className="shortcut">Ctrl+N</span>
        </MenuItem>
        <MenuItem onClick={() => handleFileAction('open')}>
          Open...
          <span className="shortcut">Ctrl+O</span>
        </MenuItem>
        <MenuDivider />
        <MenuItem onClick={() => handleFileAction('save')}>
          Save
          <span className="shortcut">Ctrl+S</span>
        </MenuItem>
        <MenuItem onClick={() => handleFileAction('saveAs')}>
          Save As...
          <span className="shortcut">Ctrl+Shift+S</span>
        </MenuItem>
        <MenuDivider />
        <MenuItem onClick={() => handleFileAction('print')}>
          Print...
          <span className="shortcut">Ctrl+P</span>
        </MenuItem>
        <MenuDivider />
        <MenuItem onClick={() => handleFileAction('share')}>
          Share
        </MenuItem>
      </FileMenuDropdown>
    </FileMenuContainer>
  );
};

// Font Menu Component
export const FontMenu = ({ isFontOpen, setIsFontOpen, execCommand }) => {
  return (
    <Select>
      <SelectButton
        onClick={() => setIsFontOpen(!isFontOpen)}
        onBlur={() => setTimeout(() => setIsFontOpen(false), 200)}
      >
        Font
      </SelectButton>
      <DropdownMenu $isOpen={isFontOpen}>
        {fonts.map(font => (
          <MenuItem
            key={font}
            fontFamily={font}
            onClick={() => {
              execCommand('fontName', font);
              setIsFontOpen(false);
            }}
          >
            {font}
          </MenuItem>
        ))}
      </DropdownMenu>
    </Select>
  );
};

// Font Size Menu Component
export const FontSizeMenu = ({ isSizeOpen, setIsSizeOpen, handleFontSize }) => {
  return (
    <Select>
      <SelectButton
        onClick={() => setIsSizeOpen(!isSizeOpen)}
        onBlur={() => setTimeout(() => setIsSizeOpen(false), 200)}
      >
        Size
      </SelectButton>
      <DropdownMenu $isOpen={isSizeOpen}>
        {fontSizes.map(size => (
          <MenuItem
            key={size}
            onClick={() => {
              handleFontSize(size);
              setIsSizeOpen(false);
            }}
          >
            {size}
          </MenuItem>
        ))}
      </DropdownMenu>
    </Select>
  );
};

// Format Menu Component
export const FormatMenu = ({ isFormatMenuOpen, setIsFormatMenuOpen, toggleFormat, execCommand, handleLink }) => {
  return (
    <ToolDropdown>
      <ToolDropdownButton
        onClick={() => setIsFormatMenuOpen(!isFormatMenuOpen)}
        onBlur={() => setTimeout(() => setIsFormatMenuOpen(false), 200)}
        title="Formatting Options"
        $isOpen={isFormatMenuOpen}
      >
        <span>Format</span>
      </ToolDropdownButton>
      <ToolDropdownMenu $isOpen={isFormatMenuOpen}>
        <ToolItemRow>
          <Button
            onClick={() => toggleFormat('bold')}
            title="Bold (Ctrl+B)"
            style={{
              fontWeight: document.queryCommandState('bold') ? 'bold' : 'normal'
            }}
          >
            B
          </Button>
          <Button
            onClick={() => toggleFormat('italic')}
            title="Italic (Ctrl+I)"
            style={{
              fontStyle: document.queryCommandState('italic') ? 'italic' : 'normal'
            }}
          >
            I
          </Button>
          <Button
            onClick={() => toggleFormat('underline')}
            title="Underline (Ctrl+U)"
            style={{
              textDecoration: document.queryCommandState('underline') ? 'underline' : 'none'
            }}
          >
            U
          </Button>
        </ToolItemRow>
        <ToolItemRow>
          <Button
            onClick={() => toggleFormat('strikeThrough')}
            title="Strike Through"
            style={{
              textDecoration: document.queryCommandState('strikeThrough') ? 'line-through' : 'none'
            }}
          >
            S
          </Button>
          <Button onClick={() => execCommand('removeFormat')} title="Clear Format">✗</Button>
          <Button onClick={handleLink} title="Insert Link (Ctrl+L)">🔗</Button>
        </ToolItemRow>
      </ToolDropdownMenu>
    </ToolDropdown>
  );
};

// Alignment Menu Component
export const AlignmentMenu = ({ isAlignMenuOpen, setIsAlignMenuOpen, execCommand }) => {
  return (
    <ToolDropdown>
      <ToolDropdownButton
        onClick={() => setIsAlignMenuOpen(!isAlignMenuOpen)}
        onBlur={() => setTimeout(() => setIsAlignMenuOpen(false), 200)}
        title="Alignment Options"
        $isOpen={isAlignMenuOpen}
      >
        <span>Align</span>
      </ToolDropdownButton>
      <ToolDropdownMenu $isOpen={isAlignMenuOpen}>
        <ToolItemRow>
          <Button onClick={() => execCommand('justifyLeft')} title="Align Left">⫷</Button>
          <Button onClick={() => execCommand('justifyCenter')} title="Center">≡</Button>
          <Button onClick={() => execCommand('justifyRight')} title="Align Right">⫸</Button>
        </ToolItemRow>
        <ToolItemRow>
          <Button onClick={() => execCommand('justifyFull')} title="Justify">☰</Button>
        </ToolItemRow>
      </ToolDropdownMenu>
    </ToolDropdown>
  );
};

// List Menu Component
export const ListMenu = ({ isListMenuOpen, setIsListMenuOpen, execCommand }) => {
  return (
    <ToolDropdown>
      <ToolDropdownButton
        onClick={() => setIsListMenuOpen(!isListMenuOpen)}
        onBlur={() => setTimeout(() => setIsListMenuOpen(false), 200)}
        title="List and Indent Options"
        $isOpen={isListMenuOpen}
      >
        <span>List</span>
      </ToolDropdownButton>
      <ToolDropdownMenu $isOpen={isListMenuOpen}>
        <ToolItemRow>
          <Button onClick={() => execCommand('insertUnorderedList')} title="Bullet List">•</Button>
          <Button onClick={() => execCommand('insertOrderedList')} title="Numbered List">1.</Button>
        </ToolItemRow>
        <ToolItemRow>
          <Button onClick={() => execCommand('indent')} title="Indent">→</Button>
          <Button onClick={() => execCommand('outdent')} title="Outdent">←</Button>
        </ToolItemRow>
      </ToolDropdownMenu>
    </ToolDropdown>
  );
};

// Image Menu Component
export const ImageMenu = ({ isImageMenuOpen, setIsImageMenuOpen, handleImageFromUrl, fileInputRef }) => {
  return (
    <ToolGroup>
      <ImageMenuContainer>
        <ImageButton
          onClick={() => setIsImageMenuOpen(!isImageMenuOpen)}
          onBlur={() => setTimeout(() => setIsImageMenuOpen(false), 200)}
          title="Insert Image"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 5h16v14H4z" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </ImageButton>
        <ImageDropdown $isOpen={isImageMenuOpen}>
          <MenuItem onClick={handleImageFromUrl}>
            From URL...
          </MenuItem>
          <MenuItem onClick={() => fileInputRef.current.click()}>
            From Computer...
          </MenuItem>
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  // Create an image element
                  const img = document.createElement('img');
                  img.src = e.target.result;
                  img.style.maxWidth = '100%';  // Make sure image fits in editor

                  // Get the current selection or focus the editor
                  const selection = window.getSelection();
                  const range = selection.getRangeAt(0);

                  // Insert the image at cursor position
                  range.insertNode(img);

                  // Move cursor after image
                  range.setStartAfter(img);
                  range.setEndAfter(img);
                  selection.removeAllRanges();
                  selection.addRange(range);

                  setIsImageMenuOpen(false);
                };
                reader.readAsDataURL(file);
              }
            }}
            accept="image/*"
            style={{ display: 'none' }}
          />
        </ImageDropdown>
      </ImageMenuContainer>
    </ToolGroup>
  );
};

// Color Menu Component
export const ColorMenu = ({ isColorMenuOpen, setIsColorMenuOpen, currentColor, currentHighlight, handleColorClick, handleHighlightClick }) => {
  return (
    <ToolDropdown>
      <ToolDropdownButtonEnhanced
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsColorMenuOpen(!isColorMenuOpen);
        }}
        onMouseDown={(e) => {
          // Prevent onBlur from firing immediately
          e.preventDefault();
        }}
        title="Color Options"
        $isOpen={isColorMenuOpen}
        data-color-button="true"
      >
        <span style={{ marginRight: '4px' }}>Color</span>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '4px'
        }}>
          <div style={{ 
            width: '12px', 
            height: '12px', 
            backgroundColor: currentColor,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '2px' 
          }} />
          <div style={{ 
            width: '12px', 
            height: '12px', 
            backgroundColor: currentHighlight,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '2px' 
          }} />
        </div>
      </ToolDropdownButtonEnhanced>
      <ToolDropdownMenu 
        $isOpen={isColorMenuOpen} 
        style={{ 
          width: '240px', 
          padding: '15px',
          position: 'absolute',
          zIndex: 9999,
          left: '0',
          top: 'calc(100% + 5px)',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
        data-color-menu="true"
      >
        <div style={{ marginBottom: '12px' }}>
          <div style={{ 
            fontSize: '12px', 
            color: 'rgba(255,255,255,0.7)', 
            marginBottom: '6px',
            fontWeight: 'bold'
          }}>
            Text Color: <span style={{ color: currentColor }}>■</span>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(8, 1fr)', 
            gap: '4px' 
          }}>
            {colors.slice(0, 16).map(color => (
              <ColorSwatch
                key={color}
                color={color}
                $isSelected={color === currentColor}
                type="button"
                title={color}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleColorClick(color);
                }}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur event on parent
                }}
              />
            ))}
          </div>
        </div>
        <div>
          <div style={{ 
            fontSize: '12px', 
            color: 'rgba(255,255,255,0.7)', 
            marginBottom: '6px',
            fontWeight: 'bold'
          }}>
            Highlight Color: <span style={{ backgroundColor: currentHighlight, padding: '0 4px' }}>A</span>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(8, 1fr)', 
            gap: '4px' 
          }}>
            {highlightColors.slice(0, 16).map(color => (
              <ColorSwatch
                key={color}
                color={color}
                $isSelected={color === currentHighlight}
                type="button"
                title={color}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleHighlightClick(color);
                }}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur event on parent
                }}
                style={{
                  boxShadow: color === '#FFFFFF' ? '0 0 0 1px rgba(0,0,0,0.1) inset' : 'none',
                }}
              />
            ))}
          </div>
        </div>
      </ToolDropdownMenu>
    </ToolDropdown>
  );
};

// Undo/Redo Component
export const UndoRedoGroup = ({ handleUndo, handleRedo }) => {
  return (
    <ToolGroup>
      <Button onClick={handleUndo} title="Undo (Ctrl+Z)">↶</Button>
      <Button onClick={handleRedo} title="Redo (Ctrl+Y)">↷</Button>
    </ToolGroup>
  );
};

// Recreate Button Component
export const RecreateButton = ({ handleRefresh }) => {
  return (
    <ToolGroup>
      <Button 
        onClick={handleRefresh} 
        title="Recreate"
        style={{
          padding: '8px 14px',
          fontSize: '15px',
          fontWeight: 'bold',
          background: 'linear-gradient(90deg, #FF8C42 0%, #F85E00 100%)',
          color: 'white',
          border: 'none',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
        }}
      >
        Recreate
      </Button>
    </ToolGroup>
  );
};

// Save Status Component
export const SaveStatusIndicator = ({ saveStatus, hasStartedEditing }) => {
  if (!hasStartedEditing || !saveStatus) return null;
  
  return (
    <ToolGroup>
      <div style={{
        fontSize: '16px',
        color: saveStatus === 'Error saving' ? '#ff4444' : '#666',
        marginLeft: '8px',
        fontWeight: 'bold'
      }}>
        {saveStatus}
      </div>
    </ToolGroup>
  );
};

// Context Menu Component
export const EditorContextMenu = ({ contextMenu, handleContextMenuAction }) => {
  if (!contextMenu.isOpen) return null;
  
  return (
    <ContextMenu 
      $isOpen={contextMenu.isOpen} 
      style={{ 
        top: `${contextMenu.y}px`, 
        left: `${contextMenu.x}px` 
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <ContextMenuItem onClick={() => handleContextMenuAction('cut')}>
        Cut
        <span className="shortcut">Ctrl+X</span>
      </ContextMenuItem>
      <ContextMenuItem onClick={() => handleContextMenuAction('copy')}>
        Copy
        <span className="shortcut">Ctrl+C</span>
      </ContextMenuItem>
      <ContextMenuItem onClick={() => handleContextMenuAction('paste')}>
        Paste
        <span className="shortcut">Ctrl+V</span>
      </ContextMenuItem>
      <MenuDivider />
      <ContextMenuItem onClick={() => handleContextMenuAction('delete')}>
        Delete
      </ContextMenuItem>
      <ContextMenuItem onClick={() => handleContextMenuAction('selectAll')}>
        Select All
        <span className="shortcut">Ctrl+A</span>
      </ContextMenuItem>
      <MenuDivider />
      <ContextMenuItem onClick={() => handleContextMenuAction('bold')}>
        Bold
        <span className="shortcut">Ctrl+B</span>
      </ContextMenuItem>
      <ContextMenuItem onClick={() => handleContextMenuAction('italic')}>
        Italic
        <span className="shortcut">Ctrl+I</span>
      </ContextMenuItem>
      <ContextMenuItem onClick={() => handleContextMenuAction('underline')}>
        Underline
        <span className="shortcut">Ctrl+U</span>
      </ContextMenuItem>
    </ContextMenu>
  );
}; 